/**
 * Sprint 19: SLA Threshold Configuration Page
 * 
 * Configurable SLA thresholds per governance stage:
 * - Intake, Assessment, Governance, Delivery
 */

import React, { useState, useEffect } from 'react';
import { 
  Clock, Save, RefreshCw, AlertTriangle, CheckCircle, 
  Timer, TrendingUp, Settings2, Info
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const SLASettingsPage = () => {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    loadConfig();
    loadStatus();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.getSLAConfig();
      setConfig(response.data?.config?.thresholds || response.data?.defaults);
      setDefaults(response.data?.defaults || {});
    } catch (error) {
      console.error('Failed to load SLA config:', error);
      toast.error('Failed to load SLA configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await api.getSLAStatus();
      setStatus(response.data?.status || {});
    } catch (error) {
      console.error('Failed to load SLA status:', error);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.updateSLAConfig({ thresholds: config });
      toast.success('SLA thresholds saved');
      loadStatus(); // Refresh status
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save configuration';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setConfig({ ...defaults });
    toast.info('Reset to defaults - save to apply');
  };

  const updateThreshold = (stageKey, field, value) => {
    const numValue = parseInt(value) || 0;
    setConfig(prev => ({
      ...prev,
      [stageKey]: {
        ...prev[stageKey],
        [field]: numValue
      }
    }));
  };

  const getStatusColor = (complianceRate) => {
    if (complianceRate >= 90) return 'text-green-600 bg-green-100';
    if (complianceRate >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="sla-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
          <Clock className="w-7 h-7 text-[var(--clr-brand-primary)]" />
          SLA Threshold Configuration
        </h1>
        <p className="text-[var(--clr-text-secondary)] mt-1">
          Configure Service Level Agreement thresholds for each governance stage
        </p>
      </div>

      {/* Current Status Summary */}
      {status && Object.keys(status).length > 0 && (
        <div className="bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            Current SLA Compliance
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(status).map(([key, data]) => (
              <div key={key} className="bg-[var(--clr-surface-subtle)] rounded-lg p-4">
                <p className="text-sm text-[var(--clr-text-secondary)] mb-1">{data.stage}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-2xl font-bold px-2 py-1 rounded ${getStatusColor(data.compliance_rate)}`}>
                    {data.compliance_rate}%
                  </span>
                  <div className="text-xs text-right text-[var(--clr-text-muted)]">
                    <p>{data.on_track} on track</p>
                    <p className="text-yellow-600">{data.at_warning} warning</p>
                    <p className="text-red-600">{data.breached} breached</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration Cards */}
      <div className="space-y-4">
        {config && Object.entries(config).map(([stageKey, stageConfig]) => (
          <div 
            key={stageKey}
            className="bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-[var(--clr-text-primary)]">
                  {stageConfig.stage || stageKey.charAt(0).toUpperCase() + stageKey.slice(1)}
                </h3>
                {stageConfig.description && (
                  <p className="text-sm text-[var(--clr-text-muted)]">{stageConfig.description}</p>
                )}
              </div>
              {status?.[stageKey] && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status[stageKey].compliance_rate)}`}>
                  {status[stageKey].compliance_rate}% Compliant
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Warning Threshold */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--clr-text-primary)] mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Warning Days
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max={stageConfig.target_days - 1}
                    value={stageConfig.warning_days || ''}
                    onChange={(e) => updateThreshold(stageKey, 'warning_days', e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--clr-border-light)] rounded-lg focus:ring-2 focus:ring-yellow-200 focus:border-yellow-400"
                    data-testid={`${stageKey}-warning-days`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--clr-text-muted)]">days</span>
                </div>
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">Alert when approaching target</p>
              </div>

              {/* Target Threshold */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--clr-text-primary)] mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Target Days
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={stageConfig.target_days || ''}
                    onChange={(e) => updateThreshold(stageKey, 'target_days', e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--clr-border-light)] rounded-lg focus:ring-2 focus:ring-green-200 focus:border-green-400"
                    data-testid={`${stageKey}-target-days`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--clr-text-muted)]">days</span>
                </div>
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">Expected completion time</p>
              </div>

              {/* Critical Threshold */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--clr-text-primary)] mb-2">
                  <Timer className="w-4 h-4 text-red-500" />
                  Critical Days
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={stageConfig.target_days + 1}
                    value={stageConfig.critical_days || ''}
                    onChange={(e) => updateThreshold(stageKey, 'critical_days', e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--clr-border-light)] rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400"
                    data-testid={`${stageKey}-critical-days`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--clr-text-muted)]">days</span>
                </div>
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">SLA breach threshold</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">How SLA Thresholds Work</p>
          <ul className="mt-1 space-y-1 list-disc list-inside text-blue-700">
            <li><span className="font-medium">Warning:</span> Items approaching target time (show yellow indicator)</li>
            <li><span className="font-medium">Target:</span> Expected completion time for the stage</li>
            <li><span className="font-medium">Critical:</span> Items exceeding this are marked as SLA breaches</li>
          </ul>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={resetToDefaults}
          className="flex items-center gap-2 px-4 py-2 text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Reset to Defaults
        </button>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[var(--clr-brand-primary)] text-white font-medium rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50 transition-colors"
          data-testid="save-sla-config"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Thresholds
        </button>
      </div>
    </div>
  );
};

export default SLASettingsPage;
